import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { AppDataProvider } from '@/app-data';
import { HistoryProvider } from '@/features/history/history';
import { IdentityProvider, RETURN_TO_KEY } from '@/features/identity/identity';
import { SignInGate } from '@/features/identity/sign-in-screen';
import { RealtimeProvider } from '@/features/realtime/realtime';
import { StatusesProvider } from '@/features/backlog/statuses';
import { EpicsProvider } from '@/features/backlog/epics';
import { WatchesProvider } from '@/features/backlog/watches';
import { FiltersProvider } from '@/features/backlog/use-filters';
import { SavedSearchesProvider } from '@/features/search/saved';
import { ItemPanelProvider } from '@/components/item-panel';
import { NewMemberProvider } from '@/components/new-member';
import { LightboxProvider } from '@/components/lightbox';
import UrlSync from '@/components/url-sync';
import DesktopHeaderProxy from '@/features/identity/desktop-header-proxy';
import { ThemeProvider } from '@/components/theme-toggle';
import AppLayout from '@/components/app-layout';
import BoardPage from '@/pages/board';
import KanbanPage from '@/pages/kanban';
import TimelinePage from '@/pages/timeline';
import MembersPage from '@/pages/members';
import SettingsPage from '@/pages/settings';
import BoardsIndexPage from '@/pages/boards';
import WhiteboardsIndexPage from '@/pages/whiteboards';
import CanvasPage from '@/pages/canvas';
import WikiPage from '@/pages/wiki';
import TaskPage from '@/pages/task';
import SearchPage from '@/pages/search';
import MyCommentsPage from '@/pages/my-comments';
import ReportsPage from '@/pages/reports';
import TrayPanel from '@/pages/tray-panel';

/**
 * OIDC return landing (/auth/callback). By the time SignInGate lets this render, login has completed
 * and the member is resolved — so we just navigate to the deep link the visitor originally requested
 * (captured in sessionStorage before the Zitadel redirect), or the boards index if there was none.
 */
function AuthCallback() {
  const navigate = useNavigate();
  useEffect(() => {
    let dest = '/';
    try {
      const rt = sessionStorage.getItem(RETURN_TO_KEY);
      sessionStorage.removeItem(RETURN_TO_KEY);
      if (rt && rt.startsWith('/') && !rt.startsWith('/auth/callback')) dest = rt;
    } catch {
      /* ignore */
    }
    navigate(dest, { replace: true });
  }, [navigate]);
  return null;
}

const App = () => (
  <BrowserRouter>
    <IdentityProvider>
      <SignInGate>
        <ThemeProvider>
        <AppDataProvider>
          <NewMemberProvider>
          <LightboxProvider>
          <WatchesProvider>
          <RealtimeProvider>
            <StatusesProvider>
            <EpicsProvider>
            <HistoryProvider>
              <FiltersProvider>
                <ItemPanelProvider>
                  <SavedSearchesProvider>
                  <UrlSync />
                  {/* Desktop app: bell + account are rendered in the tab strip; mount their sources here
                      (once, app-wide) so they work on every page — boards index, whiteboards included. */}
                  <DesktopHeaderProxy />
                  <Routes>
          {/* OIDC return: restore the originally-requested deep link, then this route is left. */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          {/* Desktop menu-bar / tray popover — a compact, chrome-less activity flyout + footer. */}
          <Route path="/tray" element={<TrayPanel />} />
          {/* Boards index (the home / landing) has no board chrome. */}
          <Route path="/" element={<BoardsIndexPage />} />
          {/* Whiteboards: a top-level area (sibling of boards). The canvas is full-bleed — no chrome. */}
          <Route path="/whiteboards" element={<WhiteboardsIndexPage />} />
          <Route path="/whiteboards/:whiteboardId" element={<CanvasPage />} />
          {/* Wiki: a top-level area (sibling of boards + whiteboards). Own chrome — a page tree rail. */}
          {/* Wiki: spaces index → space page-tree → open page, all friendly-slug. The legacy UUID form
              (/wiki/p/:pageId) redirects to the friendly URL. */}
          <Route path="/wiki" element={<WikiPage />} />
          <Route path="/wiki/p/:pageId" element={<WikiPage />} />
          <Route path="/wiki/:spaceSlug" element={<WikiPage />} />
          <Route path="/wiki/:spaceSlug/:pageSlug" element={<WikiPage />} />
          <Route element={<AppLayout />}>
            <Route path="/members" element={<MembersPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/search" element={<SearchPage />} />
            {/* Two static segments — ranks above the dynamic /:boardSlug/:itemKey, so this wins. */}
            <Route path="/me/comments" element={<MyCommentsPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            {/* Static view segments rank above the dynamic :itemKey, so /:slug/table etc. win. */}
            <Route path="/:boardSlug" element={<KanbanPage />} />
            <Route path="/:boardSlug/kanban" element={<KanbanPage />} />
            <Route path="/:boardSlug/table" element={<BoardPage />} />
            <Route path="/:boardSlug/timeline" element={<TimelinePage />} />
            <Route path="/:boardSlug/:itemKey" element={<TaskPage />} />
          </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  </SavedSearchesProvider>
                </ItemPanelProvider>
              </FiltersProvider>
            </HistoryProvider>
            </EpicsProvider>
            </StatusesProvider>
          </RealtimeProvider>
          </WatchesProvider>
          </LightboxProvider>
          </NewMemberProvider>
        </AppDataProvider>
        </ThemeProvider>
      </SignInGate>
    </IdentityProvider>
  </BrowserRouter>
);

export default App;
